import { useState, useEffect } from "react";
import { Rocket, Undo2, ExternalLink, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { resolveMediaUrl } from "../../lib/api";

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
];

const STATUS_STYLES = {
  draft: "bg-muted text-muted-foreground border-border",
  review: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  approved: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  published: "bg-primary/15 text-primary border-primary/40",
};

const wordCount = (body = []) =>
  body.reduce((n, sec) => n + (sec?.p || []).reduce((m, p) => m + String(p).split(/\s+/).filter(Boolean).length, 0), 0);

export function BlogReviewDialog({ post, onClose, onPublish, onUnpublish, publishing }) {
  const [lang, setLang] = useState("tr");

  useEffect(() => { if (post) setLang("tr"); }, [post?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const open = Boolean(post);
  if (!post) return null;

  const status = post.status || "published";
  const title = post.title?.[lang] || post.title?.en || "";
  const excerpt = post.excerpt?.[lang] || "";
  const description = post.description?.[lang] || "";
  const body = post.body?.[lang] || [];
  const faq = post.faq?.[lang] || [];
  const seo = post.seo?.[lang] || {};
  const supporting = post.supporting?.[lang] || {};
  const wc = wordCount(post.body?.tr || post.body?.en || []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        data-testid="blog-review-dialog"
        className="bg-card border-border/60 max-w-4xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-headings text-2xl leading-snug">{post.title?.tr || post.title?.en || "Post"}</DialogTitle>
              <DialogDescription className="mt-1">
                <span className="font-mono text-xs text-primary">/{post.slug}</span>
                {post.category ? <> · <span>{post.category}</span></> : null}
                {wc ? <> · <span>{wc} words (TR)</span></> : null}
              </DialogDescription>
            </div>
            <Badge className={`${STATUS_STYLES[status] || STATUS_STYLES.draft} border shrink-0`} data-testid="blog-review-status">{status}</Badge>
          </div>
        </DialogHeader>

        {post.image ? (
          <div className="mt-4 aspect-[16/9] overflow-hidden bg-secondary">
            <img src={resolveMediaUrl(post.image)} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="mt-4 border border-dashed border-border/60 bg-secondary/50 py-6 px-4 text-center font-body text-sm text-foreground/50">
            No featured image yet. Open the AI Content Studio or paste an image URL via Edit to add one.
          </div>
        )}

        <Tabs value={lang} onValueChange={setLang} className="mt-6">
          <TabsList data-testid="blog-review-lang-tabs">
            {LANGS.map((l) => (<TabsTrigger key={l.code} value={l.code} data-testid={`blog-review-lang-${l.code}`}>{l.label}</TabsTrigger>))}
          </TabsList>

          {LANGS.map((l) => (
            <TabsContent key={l.code} value={l.code} className="mt-5 space-y-5">
              <div>
                <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Title</p>
                <p className="font-headings text-lg text-foreground">{title || <span className="text-foreground/40">—</span>}</p>
              </div>
              {excerpt ? (
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Excerpt</p>
                  <p className="font-body text-sm text-foreground/85 leading-relaxed">{excerpt}</p>
                </div>
              ) : null}
              <div>
                <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">Body</p>
                <div data-testid={`blog-review-body-${l.code}`} className="space-y-4 border-l border-primary/30 pl-4">
                  {body.length ? body.map((sec, si) => (
                    <div key={sec.h2 || si}>
                      {sec.h2 ? <h4 className="font-headings text-base text-foreground mb-1.5">{sec.h2}</h4> : null}
                      {(sec.p || []).map((p, pi) => (<p key={pi} className="font-body text-sm text-foreground/80 leading-relaxed mb-2">{p}</p>))}
                      {(sec.bullets || []).length ? (
                        <ul className="list-disc pl-5 mt-1">
                          {sec.bullets.map((b, bi) => (<li key={bi} className="font-body text-sm text-foreground/80">{b}</li>))}
                        </ul>
                      ) : null}
                    </div>
                  )) : <p className="text-foreground/40 font-body text-sm">No body content in {l.label}.</p>}
                </div>
              </div>
              {faq.length ? (
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">FAQ</p>
                  <div className="space-y-3">
                    {faq.map((f, i) => (
                      <div key={f.q || i} className="border border-border/40 bg-background/50 p-3">
                        <p className="font-body text-sm font-semibold text-foreground">{f.q}</p>
                        <p className="mt-1 font-body text-sm text-foreground/75 leading-relaxed">{f.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {(seo.seo_title || seo.meta_description) ? (
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">SEO</p>
                  <div className="space-y-1.5 font-body text-sm text-foreground/75">
                    {seo.seo_title ? <p><span className="text-foreground/50">Title:</span> {seo.seo_title}</p> : null}
                    {seo.meta_description ? <p><span className="text-foreground/50">Meta:</span> {seo.meta_description}</p> : null}
                    {description && !seo.meta_description ? <p><span className="text-foreground/50">Description:</span> {description}</p> : null}
                  </div>
                </div>
              ) : description ? (
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">Description</p>
                  <p className="font-body text-sm text-foreground/75">{description}</p>
                </div>
              ) : null}

              {(supporting.linkedin_post || supporting.instagram_caption || supporting.newsletter_excerpt) ? (
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-primary mb-2">Supporting content</p>
                  <div className="space-y-3 font-body text-sm">
                    {supporting.linkedin_post ? (<div><p className="text-foreground/50 text-xs uppercase tracking-widest mb-1">LinkedIn</p><p className="text-foreground/80 whitespace-pre-line">{supporting.linkedin_post}</p></div>) : null}
                    {supporting.instagram_caption ? (<div><p className="text-foreground/50 text-xs uppercase tracking-widest mb-1">Instagram</p><p className="text-foreground/80 whitespace-pre-line">{supporting.instagram_caption}</p></div>) : null}
                    {supporting.newsletter_excerpt ? (<div><p className="text-foreground/50 text-xs uppercase tracking-widest mb-1">Newsletter</p><p className="text-foreground/80 whitespace-pre-line">{supporting.newsletter_excerpt}</p></div>) : null}
                    {supporting.featured_quote ? (<div><p className="text-foreground/50 text-xs uppercase tracking-widest mb-1">Pull quote</p><p className="text-foreground/80 italic">"{supporting.featured_quote}"</p></div>) : null}
                  </div>
                </div>
              ) : null}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-8 pt-5 border-t border-border/60 flex flex-wrap items-center gap-3">
          {status === "published" ? (
            <>
              <a
                href={`/en/blog/${post.slug}`}
                target="_blank"
                rel="noreferrer"
                data-testid="blog-review-open-public"
                className="inline-flex items-center gap-1.5 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all"
              >
                Open live <ExternalLink size={12} />
              </a>
              <button
                onClick={() => onUnpublish(post)}
                disabled={publishing}
                data-testid="blog-review-unpublish"
                className="inline-flex items-center gap-1.5 border border-border/60 text-foreground/70 hover:border-yellow-500/50 hover:text-yellow-300 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest transition-all"
              >
                {publishing ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Unpublish
              </button>
            </>
          ) : (
            <button
              onClick={() => onPublish(post)}
              disabled={publishing}
              data-testid="blog-review-publish"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-6 py-2.5 uppercase tracking-[0.2em] text-xs font-body transition-all"
            >
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />} Publish
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="blog-review-close"
            className="ml-auto inline-flex items-center gap-1.5 border border-border/60 hover:border-foreground/40 px-4 py-2 text-xs uppercase tracking-widest transition-all"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
